import { Modal, TextInput, Button, Stack, Select, Textarea, Group, Text, Divider } from '@mantine/core'
import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { volumesApi } from '@/api'
import { notifications } from '@mantine/notifications'
import { IconPlus, IconX } from '@tabler/icons-react'

interface CreateVolumeModalProps {
  opened: boolean
  onClose: () => void
  onSuccess: () => void
}

export function CreateVolumeModal({ opened, onClose, onSuccess }: CreateVolumeModalProps) {
  const [name, setName] = useState('')
  const [driver, setDriver] = useState<string>('local')
  const [labels, setLabels] = useState<Array<{ key: string; value: string }>>([])
  const [driverOpts, setDriverOpts] = useState<Array<{ key: string; value: string }>>([])

  const driverOptions = [
    { value: 'local', label: 'local' },
    { value: 'nfs', label: 'nfs' },
    { value: 'cifs', label: 'cifs' },
    { value: 'glusterfs', label: 'glusterfs' },
    { value: 'ceph', label: 'ceph' },
  ]

  const createMutation = useMutation({
    mutationFn: async () => {
      const labelsObj = labels.reduce((acc, { key, value }) => {
        if (key && value) acc[key] = value
        return acc
      }, {} as Record<string, string>)

      const optsObj = driverOpts.reduce((acc, { key, value }) => {
        if (key && value) acc[key] = value
        return acc
      }, {} as Record<string, string>)

      const { data } = await volumesApi.create({
        Name: name,
        Driver: driver,
        Labels: Object.keys(labelsObj).length > 0 ? labelsObj : undefined,
        DriverOpts: Object.keys(optsObj).length > 0 ? optsObj : undefined,
      })
      return data
    },
    onSuccess: () => {
      notifications.show({
        title: 'Volume created',
        message: `Volume ${name} has been created successfully`,
        color: 'green',
      })
      onSuccess()
      resetForm()
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.message || 'Failed to create volume',
        color: 'red',
      })
    },
  })

  const resetForm = () => {
    setName('')
    setDriver('local')
    setLabels([])
    setDriverOpts([])
  }

  const handleSubmit = () => {
    if (!name) {
      notifications.show({
        title: 'Validation error',
        message: 'Volume name is required',
        color: 'red',
      })
      return
    }

    // Validate volume name format
    if (!/^[a-zA-Z0-9_.-]+$/.test(name)) {
      notifications.show({
        title: 'Validation error',
        message: 'Volume name must be alphanumeric, underscore, hyphen, or dot',
        color: 'red',
      })
      return
    }

    createMutation.mutate()
  }

  const addLabel = () => {
    setLabels([...labels, { key: '', value: '' }])
  }

  const removeLabel = (index: number) => {
    setLabels(labels.filter((_, i) => i !== index))
  }

  const updateLabel = (index: number, field: 'key' | 'value', value: string) => {
    const newLabels = [...labels]
    newLabels[index][field] = value
    setLabels(newLabels)
  }

  const addDriverOpt = () => {
    setDriverOpts([...driverOpts, { key: '', value: '' }])
  }

  const removeDriverOpt = (index: number) => {
    setDriverOpts(driverOpts.filter((_, i) => i !== index))
  }

  const updateDriverOpt = (index: number, field: 'key' | 'value', value: string) => {
    const newOpts = [...driverOpts]
    newOpts[index][field] = value
    setDriverOpts(newOpts)
  }

  return (
    <Modal opened={opened} onClose={onClose} title="Create Volume" size="lg">
      <Stack>
        <TextInput
          label="Volume Name"
          placeholder="my-volume"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          required
          description="Alphanumeric, underscore, hyphen, or dot characters only"
        />

        <Select
          label="Driver"
          data={driverOptions}
          value={driver}
          onChange={(value) => setDriver(value || 'local')}
          description="Storage driver for the volume"
        />

        <Divider label="Labels (optional)" labelPosition="left" />

        {labels.map((label, index) => (
          <Group key={index} align="flex-end">
            <TextInput
              style={{ flex: 1 }}
              placeholder="key"
              value={label.key}
              onChange={(e) => updateLabel(index, 'key', e.currentTarget.value)}
            />
            <TextInput
              style={{ flex: 1 }}
              placeholder="value"
              value={label.value}
              onChange={(e) => updateLabel(index, 'value', e.currentTarget.value)}
            />
            <Button
              variant="subtle"
              color="red"
              onClick={() => removeLabel(index)}
              leftSection={<IconX size={14} />}
            >
              Remove
            </Button>
          </Group>
        ))}

        <Button
          variant="light"
          leftSection={<IconPlus size={14} />}
          onClick={addLabel}
        >
          Add Label
        </Button>

        <Divider label="Driver Options (optional)" labelPosition="left" />

        <Text size="xs" c="dimmed">
          {driver === 'nfs' && 'NFS requires "device" option (e.g., 192.168.1.10:/path/to/share)'}
          {driver === 'cifs' && 'CIFS requires "device" option (e.g., //server/share)'}
          {driver === 'local' && 'Local driver supports: type, size, device, o (mount options)'}
        </Text>

        {driverOpts.map((opt, index) => (
          <Group key={index} align="flex-end">
            <TextInput
              style={{ flex: 1 }}
              placeholder="key"
              value={opt.key}
              onChange={(e) => updateDriverOpt(index, 'key', e.currentTarget.value)}
            />
            <TextInput
              style={{ flex: 1 }}
              placeholder="value"
              value={opt.value}
              onChange={(e) => updateDriverOpt(index, 'value', e.currentTarget.value)}
            />
            <Button
              variant="subtle"
              color="red"
              onClick={() => removeDriverOpt(index)}
              leftSection={<IconX size={14} />}
            >
              Remove
            </Button>
          </Group>
        ))}

        <Button
          variant="light"
          leftSection={<IconPlus size={14} />}
          onClick={addDriverOpt}
        >
          Add Driver Option
        </Button>

        <Button
          fullWidth
          onClick={handleSubmit}
          loading={createMutation.isPending}
          mt="md"
        >
          Create Volume
        </Button>
      </Stack>
    </Modal>
  )
}
